import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroBanner from './components/HeroBanner';
import CategoryNav from './components/CategoryNav';
import HotComics from './components/HotComics';
import LatestUpdates from './components/LatestUpdates';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '叮当漫画网',
  url: 'https://dingdangmh.com',
  description: '叮当漫画网推出大黄丫头漫画无删减版，下拉式免费阅读，海量漫画每日更新。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@dingdangmh.com',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '叮当漫画网',
  url: 'https://dingdangmh.com',
  description: '大黄丫头漫画无删减版下拉式免费阅读平台',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://dingdangmh.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '叮当漫画网 - 首页',
  description: '叮当漫画网推出大黄丫头漫画无删减版，下拉式免费阅读，所有内容完整呈现，无需付费。',
  url: 'https://dingdangmh.com/',
  isPartOf: {
    '@type': 'WebSite',
    name: '叮当漫画网',
    url: 'https://dingdangmh.com',
  },
};

export default function Home() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subMessage, setSubMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setSubStatus('success');
      setSubMessage('订阅成功！感谢您的关注。');
      setEmail('');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9ot9iiftmtnfcfh0dj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* raw text */ }
      const code = (parsed as { code?: string })?.code;
      if (res.ok && code === 'OK') {
        setSubStatus('success');
        setSubMessage('订阅成功！感谢您的关注，我们会将最新漫画资讯发送到您的邮箱。');
        setEmail('');
      } else {
        const msg = (parsed as { meta?: { message?: string }; message?: string })?.meta?.message
          || (parsed as { message?: string })?.message
          || text;
        throw new Error(String(msg));
      }
    } catch (err: unknown) {
      setSubStatus('error');
      const msg = err instanceof Error ? err.message : '订阅失败，请稍后再试';
      setSubMessage(msg.includes('spam') ? '订阅失败，请稍后再试' : msg);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main>
        <HeroBanner />
        <CategoryNav />
        <HotComics />
        <LatestUpdates />

        {/* Features Section */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="features-title">
          <div className="text-center mb-10">
            <h2 id="features-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide">
              为什么选择叮当漫画网
            </h2>
            <p className="text-sm text-foreground-500 mt-1">为漫画爱好者打造的极致阅读体验</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-shield-check-line text-xl text-primary-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">无删减完整版</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">所有漫画内容完整呈现，不删减、不修改，原汁原味的阅读体验。</p>
            </div>
            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-smartphone-line text-xl text-accent-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">下拉式阅读</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">创新的下拉式阅读方式，无需翻页，一气呵成，沉浸式追漫体验。</p>
            </div>
            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-coin-line text-xl text-secondary-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-800 mb-2">完全免费</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">无需付费、无需会员，所有漫画资源永久免费，让每个人都能够畅快追漫。</p>
            </div>
          </div>
        </section>

        {/* CTA / Newsletter */}
        <section className="w-full bg-background-100 py-14 md:py-18" aria-labelledby="cta-title">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 id="cta-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide mb-3">
              订阅我们的更新通知
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto">
              第一时间获取大黄丫头漫画的最新章节和叮当漫画网的更新资讯，精彩不错过！
            </p>
            <form
              className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
              onSubmit={handleSubscribe}
              data-readdy-form
            >
              <div className="spm-field-wrap">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 w-full sm:w-auto px-5 py-3 bg-background-50 border border-background-300/60 rounded-full text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                免费订阅
              </button>
            </form>
            {subStatus !== 'idle' && (
              <p className={`mt-4 text-sm ${subStatus === 'success' ? 'text-primary-600' : 'text-accent-600'}`}>
                {subMessage}
              </p>
            )}
          </div>
        </section>

        {/* Quick links to categories */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="explore-title">
          <div className="text-center mb-8">
            <h2 id="explore-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide">
              探索更多分类
            </h2>
            <p className="text-sm text-foreground-500 mt-1">八大漫画分类，总有一款适合你</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {[
              { name: '热血漫画', slug: 'rexue', icon: 'ri-fire-line' },
              { name: '搞笑漫画', slug: 'gaoxiao', icon: 'ri-emotion-laugh-line' },
              { name: '恋爱漫画', slug: 'lianai', icon: 'ri-heart-line' },
              { name: '奇幻漫画', slug: 'qihuan', icon: 'ri-magic-line' },
              { name: '悬疑漫画', slug: 'xuanyi', icon: 'ri-search-eye-line' },
              { name: '校园漫画', slug: 'xiaoyuan', icon: 'ri-school-line' },
              { name: '武侠漫画', slug: 'wuxia', icon: 'ri-sword-line' },
              { name: '科幻漫画', slug: 'kehuan', icon: 'ri-rocket-line' },
            ].map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="flex items-center gap-3 p-4 bg-background-100 hover:bg-primary-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-200 transition-colors">
                  <i className={`${item.icon} text-lg text-primary-500`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors whitespace-nowrap">
                  {item.name}
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}